import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, X, ShoppingCart, Search, Check } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

/**
 * Convert a custom catalog into a draft order for a chosen customer.
 * - Customer picker (searches /admin/users, active accounts first).
 * - Default quantity per SKU (1 by default, editable after creation).
 * - Optional toggle to honor the catalog's own discount_pct instead of the
 *   customer's account discount.
 * On success we redirect the admin to the new order detail page.
 */
export default function CatalogConvertDialog({ catalog, open, onOpenChange, onConverted }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [qty, setQty] = useState('1');
  const [useCatalogPricing, setUseCatalogPricing] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery(''); setSelectedId(''); setQty('1'); setNotes('');
    setUseCatalogPricing(true);
    setLoadingUsers(true);
    api.get('/admin/users')
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load customers'))
      .finally(() => setLoadingUsers(false));
  }, [open]);

  const catalogDiscount = Number(catalog?.discount_pct || 0);
  const catalogHasDiscount = catalog?.pricing_mode === 'discounted' && catalogDiscount > 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = users.filter((u) => u.status === 'active');
    if (!q) return base.slice(0, 40);
    return base.filter((u) =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.business_name || '').toLowerCase().includes(q) ||
      (u.contact_name || '').toLowerCase().includes(q),
    ).slice(0, 40);
  }, [users, query]);

  const selected = users.find((u) => u.id === selectedId);
  const itemCount = (catalog?.item_skus || []).length;

  const convert = async () => {
    if (!selectedId) {
      toast.info('Pick a customer first');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/catalogs/${catalog.id}/convert-to-order`, {
        user_id: selectedId,
        default_qty: Math.max(1, parseInt(qty, 10) || 1),
        use_catalog_pricing: useCatalogPricing,
        notes,
      });
      const bits = [`${data.lines_added} line${data.lines_added === 1 ? '' : 's'} added`];
      if (data.skipped_skus?.length) {
        bits.push(`${data.skipped_skus.length} SKU${data.skipped_skus.length === 1 ? '' : 's'} skipped`);
      }
      toast.success(`Draft order created — ${bits.join(' · ')}`);
      onConverted?.(data.order);
      onOpenChange(false);
      navigate(`/orders/${data.order.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Convert failed');
    } finally { setBusy(false); }
  };

  if (!catalog) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-16px)] max-w-xl max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3 sm:gap-4 !grid-cols-[minmax(0,1fr)]" data-testid="catalog-convert-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-xl sm:text-2xl">Convert to Order</DialogTitle>
          <p className="sans text-[10px] sm:text-xs tracking-widest uppercase text-neutral-500">
            {catalog.name} · {itemCount} item{itemCount === 1 ? '' : 's'}
          </p>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Customer picker */}
          <div>
            <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Customer</label>
            <div className="relative">
              {!query && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />}
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by business name, contact, or email…"
                className="ije-input h-10 w-full pl-9"
                data-testid="convert-customer-search"
              />
            </div>
            <div className="mt-2 border border-neutral-200 max-h-60 overflow-y-auto divide-y divide-neutral-100" data-testid="convert-customer-list">
              {loadingUsers ? (
                <p className="sans text-sm text-neutral-500 p-4">Loading customers…</p>
              ) : filtered.length === 0 ? (
                <p className="sans text-sm text-neutral-500 p-4">No active customers match.</p>
              ) : (
                filtered.map((u) => {
                  const active = u.id === selectedId;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setSelectedId(u.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${active ? 'bg-[color:var(--ije-pink-tint)]' : 'hover:bg-neutral-50'}`}
                      data-testid={`convert-customer-option-${u.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="sans text-sm text-neutral-900 truncate">
                          {u.business_name || u.contact_name || u.email}
                        </p>
                        <p className="sans text-[11px] text-neutral-500 truncate">
                          {u.email}
                          {typeof u.discount_percent === 'number' && u.discount_percent > 0
                            ? ` · ${u.discount_percent}% off wholesale`
                            : ''}
                        </p>
                      </div>
                      {active && <Check size={14} className="text-black flex-shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Qty */}
          <div>
            <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">
              Default quantity per line
            </label>
            <input
              type="number" min="1" step="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="ije-input h-10 w-32"
              data-testid="convert-default-qty"
            />
            <p className="sans text-[11px] text-neutral-500 mt-1">
              You can adjust individual line quantities after the draft is created.
            </p>
          </div>

          {/* Pricing */}
          <div>
            <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Pricing</label>
            {catalogHasDiscount ? (
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={useCatalogPricing}
                  onChange={(e) => setUseCatalogPricing(e.target.checked)}
                  className="mt-1 accent-black"
                  data-testid="convert-use-catalog-pricing"
                />
                <span className="sans text-sm text-neutral-700">
                  Apply this catalog's {catalogDiscount}% discount
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Uncheck to use{' '}
                    {selected ? (selected.business_name || selected.email) : "the customer's"} own account discount
                    {selected && ` (${selected.discount_percent || 0}% off wholesale)`}.
                  </span>
                </span>
              </label>
            ) : (
              <p className="sans text-sm text-neutral-500">
                This catalog uses straight wholesale. The order will apply
                {selected ? ` ${selected.business_name || selected.email}'s` : " the customer's"}
                {' '}account discount ({selected ? (selected.discount_percent || 0) : 0}% off wholesale).
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything to include on the draft order…"
              className="ije-input w-full p-3 resize-none"
              data-testid="convert-notes"
            />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto"
            data-testid="convert-cancel"
          >
            <X size={14} className="inline mr-1" /> Cancel
          </button>
          <button
            type="button"
            onClick={convert}
            disabled={busy || !selectedId || itemCount === 0}
            className="btn-primary h-11 sm:h-10 w-full sm:w-auto"
            data-testid="convert-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
            Create draft order
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
